import { p as proxyCustomElement, H, c as createEvent, h, d as Host } from './p-bfa0dc67.js';
import { c as classNames } from './p-df8c8ec2.js';
import { i as inheritAriaAttributes } from './p-198613ef.js';
import { m as messageId, V as Validation } from './p-319941a1.js';

const biCheckboxCss = ".sr-only{position:absolute;height:1px;width:1px;padding:0;margin:-1px;overflow:hidden;border:0;left:-9999px;text-indent:-5000px;clip:rect(1px, 1px, 1px, 1px);}*,*:before,*:after{box-sizing:border-box}html,body,div,span,object,iframe,figure,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,code,em,img,small,strike,strong,sub,sup,tt,b,u,i,ol,ul,li,fieldset,form,label,table,caption,tbody,tfoot,thead,tr,th,td,main,canvas,embed,footer,header,nav,section,button,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;text-size-adjust:none}footer,header,nav,section,main{display:block}body{line-height:1.5}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:before,blockquote:after,q:before,q:after{content:\"\";content:none}table{border-collapse:collapse;border-spacing:0}input{-webkit-appearance:none;appearance:none;border-radius:0}:host(:focus){outline:none !important;border:none !important}.validation-message{font-size:1rem;color:#8c0a08;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;width:100%;height:1.5rem;margin-top:12px;margin-bottom:12px;display:block}.validation-message.dark{color:#ec9a98}.validation-message.noMargin{margin:0}:host{width:fit-content}:host(:focus){outline:none;border:none}.wrapper{font-size:1rem;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;font-weight:400;display:flex;flex-direction:column}.wrapper.disabled{opacity:0.3;cursor:not-allowed}.container{font-family:\"museo-sans\", arial, sans-serif;font-weight:300;font-size:1rem;line-height:1.5;position:relative;align-items:center;display:flex;gap:0.5rem;user-select:none;width:fit-content;min-height:1.5rem;border:2px dashed transparent;cursor:pointer;--text-color:var(--bi-neutral-90);--bg-color:var(--bi-primary-50);--bg-hover-color:#26333f1a;--border-color:var(--bi-neutral-50);--border-hover-color:#00305c;--border-error-color:#8c0a08;--checkmark-color:white;--checkmark-stroke-color:white;--required-color:#8c0a08;color:var(--text-color)}.container:has(input:disabled){cursor:default}.container.dark{color-scheme:dark;--text-color:var(--bi-basic-white);--bg-hover-color:#ffffff1a;--border-color:var(--bi-neutral-40);--border-hover-color:#e6f2fa;--border-error-color:#ec9a98;--required-color:#ec9a98}.container .required{color:var(--required-color)}.container input{position:absolute;pointer-events:none;opacity:0;margin:0px;width:16px;height:16px}.container input~div>svg{border:2px solid var(--border-color);border-radius:var(--bi-scale-0-50x)}.container input~div>svg path{fill:transparent;stroke:transparent}.container .checkmark-container{height:var(--bi-scale-9x);width:var(--bi-scale-9x);min-width:var(--bi-scale-9x);display:flex;justify-content:center;align-items:center;border-radius:var(--bi-scale-0-25x)}.container:hover input~.checkmark-container{background-color:var(--bg-hover-color)}.container input:checked~div>svg{background-color:var(--bg-color);border-color:var(--bg-color)}.container input:checked~div>svg path{fill:var(--checkmark-stroke-color);stroke:var(--checkmark-stroke-color)}.container input:indeterminate~div>svg{background-color:var(--bg-color);border-color:var(--bg-color)}.container input:indeterminate~div>svg path{fill:var(--checkmark-stroke-color);stroke:var(--checkmark-stroke-color)}:host(:focus):host(:not(:active)) svg{box-shadow:0 0 0 2px var(--checkmark-color), 0 0 0 4px var(--bg-color), 0 0 0 6px var(--checkmark-color)}";
const BiCheckboxStyle0 = biCheckboxCss;

const BiCheckbox = /*@__PURE__*/ proxyCustomElement(class BiCheckbox extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.change = createEvent(this, "change", 7);
        this.blur = createEvent(this, "blur", 7);
        this.internals = this.attachInternals?.();
        this.inheritedAttributes = {};
        // The "name" property is reflected as an attribute to ensure compatibility with native form submission.
        // In the React wrapper, all props are synced as properties on the element ref, so reflecting "name" as an attribute ensures it is properly handled in the form submission process.
        /**
         * The checkbox value.
         * When a form is submitted, only a checkbox which is currently checked is included in the submission.
         * @default 'on'
         */
        this.value = 'on';
        /** Marks the checkbox field as required. */
        this.required = false;
        /** Marks the checkbox field as disabled. */
        this.disabled = false;
        /** Is the component displayed on a dark background. */
        this.dark = false;
        /** If `true`, the checkbox will be in an indeterminate state. Used when a checkbox is part of a group of checkboxes and some of the checkboxes are checked, but not all of them. */
        this.indeterminate = false;
        /**
         * If `true`, the component will indicate an error.
         * @default false
         */
        this.error = false;
        this.onBlur = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.blur.emit(e);
        };
        this.onChange = (e) => {
            const checked = e.target.checked;
            this.checked = checked; // triggers @Watch('checked')
            this.internals?.setFormValue(checked ? this.value : undefined);
            this.change.emit(e);
        };
    }
    get type() {
        return 'checkbox';
    }
    onKeydown(e) {
        const { key } = e;
        if ((key === ' ' || key === 'Spacebar') && this.disabled) {
            e.preventDefault();
        }
    }
    onValueChange(newValue) {
        this.internals?.setFormValue(this.checkboxInputElement?.checked ? newValue : undefined);
    }
    onCheckedChange(newValue) {
        this.internals?.setFormValue(newValue ? this.value : undefined);
    }
    componentWillLoad() {
        if (this.checked !== undefined) {
            this.defaultChecked = this.checked;
        }
        else {
            this.checked = this.defaultChecked ?? false;
        }
        this.inheritedAttributes = inheritAriaAttributes(this.el);
    }
    formResetCallback() {
        this.internals?.setFormValue(this.defaultChecked ? this.value : undefined);
        this.checked = this.defaultChecked;
    }
    formStateRestoreCallback(state) {
        this.checked = Boolean(state);
    }
    componentDidLoad() {
        if (this.checkboxInputElement.checked) {
            this.internals?.setFormValue(this.value);
        }
    }
    componentDidRender() {
        // Skip validation if the checkbox is disabled; it's ignored in form validation
        // and always has an empty validationMessage, even if some ValidityState flags are true.
        if (!this.disabled) {
            this.internals?.setValidity(this.checkboxInputElement.validity, this.checkboxInputElement.validationMessage, this.checkboxInputElement);
        }
    }
    render() {
        const id = 'checkbox';
        return (h(Host, { key: '4bd2979b755443eaafcd8cc32e65f5b428d3e9dd' }, h("div", { key: 'a87ae528db68863321bf9c7d417f36cd61f2fb4f', class: classNames('wrapper', this.disabled && 'disabled') }, h("label", { key: '2f4157644578febdaa35f7dec3f667c7adade4fd', class: classNames('container', this.dark && 'dark') }, h("input", { key: 'e260488a6415acec6f92817298bad5caa7194f66', type: 'checkbox', id: id, "aria-describedby": `${messageId}`, "aria-invalid": String(this.error), "aria-disabled": String(this.disabled), defaultChecked: this.defaultChecked, checked: this.checked, value: this.value, name: this.name, onChange: this.onChange, onBlur: this.onBlur, required: this.required, disabled: this.disabled, indeterminate: this.indeterminate, ref: (el) => (this.checkboxInputElement = el), ...this.inheritedAttributes }), h("div", { key: '3137991b3a978275f57268b1a3b1c369d34e5eb1', class: classNames('checkmark-container', { invalid: this.error }) }, h("svg", { key: 'd41206f81c731152c8efa04f831bfeff1723bb17', width: '18', height: '18', viewBox: '0 0 18 18', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, this.indeterminate ? (h("path", { d: 'M2,9 L16,9', stroke: 'currentColor', "stroke-width": '2.5' })) : (h("path", { d: 'M5.49997 9.55364L14.6437 0.504217L15.5229 1.39267L5.49997 11.3123L0.47699 6.34112L1.35628 5.45267L5.49997 9.55364Z', transform: 'translate(1, 2.5) scale(1.03, 1.03)', "stroke-width": '1.4' })))), this.label ? (h("p", null, this.label, ' ', this.required && (h("span", { class: 'required', "aria-hidden": 'true' }, ' ', "*")))) : (h("slot", null))), h(Validation, { key: '1476997b97d59b3c85dbb59866b1e9fcfd3a9a31', dark: this.dark, visible: this.error, message: this.errorMessage }))));
    }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    get el() { return this; }
    static get watchers() { return {
        "value": ["onValueChange"],
        "checked": ["onCheckedChange"]
    }; }
    static get style() { return BiCheckboxStyle0; }
}, [81, "bi-checkbox", {
        "type": [2049],
        "label": [1],
        "name": [513],
        "value": [1],
        "required": [4],
        "disabled": [4],
        "defaultChecked": [4, "default-checked"],
        "checked": [1028],
        "dark": [4],
        "errorMessage": [1, "error-message"],
        "indeterminate": [4],
        "error": [4]
    }, [[0, "keydown", "onKeydown"]], {
        "value": ["onValueChange"],
        "checked": ["onCheckedChange"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bi-checkbox"];
    components.forEach(tagName => { switch (tagName) {
        case "bi-checkbox":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BiCheckbox);
            }
            break;
    } });
}

export { BiCheckbox as B, defineCustomElement as d };
